/**
 * UPT-2124 J2d (spec `web/SPEC-UPT-2124-J2d.md` §2 n° 2-3) — la carte de CONFIRMATION (« la voix agit ») : ce qui va être fait (titre, lignes
 * « libellé · valeur » — code, nom, résumé —, la question dite), puis « Créer » / « Annuler » tant que la confirmation attend ; « Proposition
 * expirée » sans réponse en 60 s ; une fois fait, le titre change (« Dossier créé ») et la carte garde le lien vers ce qui a été créé.
 * `aria-live="polite"` : une seule annonce à son apparition (J-39).
 *
 * `glass` : rendue dans le dock Géode (verre dépoli, classes `.geode-*`) ; sinon, carte ordinaire aux jetons `portal-*` (le `VoicePanel`).
 * Les boutons ne sont rendus que si un geste est fourni ET que la confirmation attend (le dock et le panneau les branchent sur `voiceConfirm`).
 * Générique : la carte ne connaît ni Module Studio ni le dossier — tout est dans la charge, déjà traduit par l'outil qui l'a posée.
 */
import { Link } from 'react-router';
import { useIntl } from '@/react-intl';
import { cn } from '@/design-system/ds-0/components/cn';
import type { VoiceCard } from './voiceTypes';

export type VoiceConfirmCardProps = {
  card: VoiceCard;
  /** « Créer » : la confirmation en attente est tranchée « oui ». */
  onConfirm?: () => void;
  /** « Annuler » : tranchée « non », rien n'est écrit. */
  onCancel?: () => void;
  /** Verre dépoli du dock Géode ; sinon jetons du thème. */
  glass?: boolean;
  className?: string;
};

export function VoiceConfirmCard({ card, onConfirm, onCancel, glass = false, className }: VoiceConfirmCardProps) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const payload = card.confirm;
  // i18n-exempt: pas un état vide — une carte sans charge de confirmation n'est pas de ce type (le panneau rend alors la carte ordinaire).
  // eslint-disable-next-line no-restricted-syntax
  if (!payload) return null;

  const pending = payload.status === 'pending';
  const muted = glass ? 'geode-ink-muted' : 'text-portal-ink-muted';
  const ink = glass ? 'geode-ink' : 'text-portal-ink';

  return (
    <section
      aria-live="polite"
      aria-label={card.title}
      data-voice-confirm={payload.status}
      className={cn(
        'flex flex-col gap-2',
        glass ? cn('geode-glass p-4', pending && 'geode-glass--active') : 'rounded-md border border-portal-border bg-portal-surface px-3 py-2 text-portal-ink',
        className
      )}
    >
      <h3 className={cn('m-0 break-words text-lg font-semibold tracking-tight', ink)}>{card.title}</h3>
      {payload.fields.length > 0 ? (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm" data-voice-confirm-fields>
          {payload.fields.map((field, index) => (
            <div key={index} className="contents">
              <dt className={cn('m-0 font-semibold', muted)}>{field.label}</dt>
              <dd
                className={cn('m-0 min-w-0 break-words', ink, field.mono && 'font-mono text-xs')}
                dir={field.mono ? 'ltr' : undefined}
              >
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {pending ? (
        <p className={cn('m-0 text-sm', ink)} data-voice-confirm-question>
          {payload.question}
        </p>
      ) : null}
      {payload.status === 'expired' ? (
        <p className={cn('m-0 text-sm font-semibold', muted)} data-voice-confirm-expired>
          {t('voice.confirm.expired')}
        </p>
      ) : null}
      {payload.status === 'done' && payload.link ? (
        <Link
          to={payload.link.href}
          data-voice-confirm-link
          className={cn(
            'inline-flex min-h-11 items-center self-start',
            glass ? 'geode-glass geode-button geode-focus' : 'rounded-md border border-portal-border bg-portal-surface px-3 py-2 text-sm font-medium text-portal-ink'
          )}
        >
          {payload.link.label}
        </Link>
      ) : null}
      {pending && (onConfirm || onCancel) ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {onConfirm ? (
            <button
              type="button"
              onClick={onConfirm}
              data-voice-confirm-yes
              className={cn(
                'min-h-11',
                glass
                  ? 'geode-glass geode-button geode-button--accent geode-focus'
                  : 'inline-flex items-center rounded-md bg-portal-accent px-3 py-2 text-sm font-semibold text-portal-ink-on-accent'
              )}
            >
              {payload.confirmLabel ?? t('voice.confirm.create')}
            </button>
          ) : null}
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              data-voice-confirm-no
              className={cn(
                'min-h-11',
                glass
                  ? 'geode-glass geode-button geode-focus'
                  : 'inline-flex items-center rounded-md border border-portal-border bg-portal-surface px-3 py-2 text-sm font-medium text-portal-ink'
              )}
            >
              {t('voice.confirm.cancel')}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default VoiceConfirmCard;
